import type { Scene } from "../../types/scene";
import type {
  KeyframeDetailsMap,
  KeyframeMap,
  PreservedMjcfSections,
} from "../../stores/fileStore/types";

export const CRITTER_PROJECT_SNAPSHOT_FILENAME = "critter_project.json";
export const CURRENT_PROJECT_SNAPSHOT_SCHEMA_VERSION = 1;

export type CritterProjectSnapshotV1 = {
  type: "critterapp-project";
  schemaVersion: 1;
  createdAtIso: string;
  project: {
    name: string;
  };
  canonical: {
    scene: "scene";
    modelXml: string | null;
    sceneConfig: string;
  };
  scene: Scene;
  fileState: {
    referencedFiles: string[];
    keyframes: KeyframeMap;
    keyframeDetails: KeyframeDetailsMap;
    preservedMjcfSections: PreservedMjcfSections;
  };
};

const cloneJson = <T>(value: T): T => JSON.parse(JSON.stringify(value ?? null)) as T;

const asRecord = <T>(value: unknown, fallback: Record<string, T>): Record<string, T> =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, T>)
    : fallback;

const asStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.map((entry) => String(entry || "")).filter(Boolean) : [];

const normalizeSceneJointRefs = (scene: Scene): Scene => {
  const cloned = cloneJson(scene) as Scene & {
    bodies?: Record<string, { joints?: unknown[] }>;
    joints?: Record<string, { uuid?: unknown; name?: unknown } | undefined>;
  };
  const joints: Record<string, { uuid?: unknown; name?: unknown } | undefined> =
    cloned?.joints && typeof cloned.joints === "object" ? cloned.joints : {};
  const jointUuidByName = new Map<string, string>();
  Object.entries(joints || {}).forEach(([uuid, joint]) => {
    const name = typeof joint?.name === "string" ? joint.name.trim() : "";
    if (!name || jointUuidByName.has(name)) return;
    jointUuidByName.set(name, String(joint?.uuid || uuid));
  });

  Object.entries(cloned?.bodies || {}).forEach(([, body]) => {
    if (!body || !Array.isArray(body.joints)) return;
    body.joints = body.joints
      .map((entry: unknown) => {
        if (typeof entry === "string") return joints[entry] ? entry : null;
        if (!entry || typeof entry !== "object") return null;
        const ref = entry as { uuid?: unknown; name?: unknown };
        const uuid = typeof ref.uuid === "string" ? ref.uuid : "";
        if (uuid && joints[uuid]) return uuid;
        const name = typeof ref.name === "string" ? ref.name.trim() : "";
        return name ? jointUuidByName.get(name) || null : null;
      })
      .filter((entry): entry is string => Boolean(entry)) as unknown[];
  });

  return cloned as Scene;
};

const normalizeProjectSnapshotV1 = (
  obj: Record<string, unknown>
): CritterProjectSnapshotV1 | null => {
  const scene = obj.scene;
  if (!scene || typeof scene !== "object") return null;

  const project = asRecord(obj.project, {});
  const canonical = asRecord(obj.canonical, {});
  const fileState = asRecord(obj.fileState, {});

  return {
    type: "critterapp-project",
    schemaVersion: CURRENT_PROJECT_SNAPSHOT_SCHEMA_VERSION,
    createdAtIso:
      typeof obj.createdAtIso === "string" ? obj.createdAtIso : new Date(0).toISOString(),
    project: {
      name:
        typeof project.name === "string" && project.name.trim() ? project.name : "Untitled Scene",
    },
    canonical: {
      scene: "scene",
      modelXml: typeof canonical.modelXml === "string" ? canonical.modelXml : null,
      sceneConfig:
        typeof canonical.sceneConfig === "string" && canonical.sceneConfig.trim()
          ? canonical.sceneConfig
          : "scene_config.json",
    },
    scene: normalizeSceneJointRefs(scene as Scene),
    fileState: {
      referencedFiles: asStringArray(fileState.referencedFiles),
      keyframes: cloneJson(asRecord<number[]>(fileState.keyframes, {})),
      keyframeDetails: cloneJson(asRecord(fileState.keyframeDetails, {})),
      preservedMjcfSections: cloneJson(asRecord(fileState.preservedMjcfSections, {})),
    },
  };
};

const migrateProjectSnapshotV1ToCurrent = (
  obj: Record<string, unknown>
): CritterProjectSnapshotV1 | null => {
  // Identity rule: project snapshot migrations must preserve existing scene UUIDs.
  // Remapping belongs only in explicit duplicate/import-as-new code paths.
  return normalizeProjectSnapshotV1(obj);
};

export function buildProjectSnapshot(args: {
  scene: Scene;
  projectName: string;
  modelXmlPath?: string | null;
  referencedFiles?: string[];
  keyframes?: KeyframeMap;
  keyframeDetails?: KeyframeDetailsMap;
  preservedMjcfSections?: PreservedMjcfSections;
}): CritterProjectSnapshotV1 {
  return {
    type: "critterapp-project",
    schemaVersion: 1,
    createdAtIso: new Date().toISOString(),
    project: {
      name: String(args.projectName || "Untitled Scene"),
    },
    canonical: {
      scene: "scene",
      modelXml: args.modelXmlPath || null,
      sceneConfig: "scene_config.json",
    },
    scene: normalizeSceneJointRefs(args.scene),
    fileState: {
      referencedFiles: asStringArray(args.referencedFiles),
      keyframes: cloneJson(asRecord<number[]>(args.keyframes, {})),
      keyframeDetails: cloneJson(asRecord(args.keyframeDetails, {})),
      preservedMjcfSections: cloneJson(asRecord(args.preservedMjcfSections, {})),
    },
  };
}

export function migrateProjectSnapshot(raw: unknown): CritterProjectSnapshotV1 | null {
  if (!raw || typeof raw !== "object") return null;
  const obj = raw as Record<string, unknown>;
  if (obj.type !== "critterapp-project") return null;
  const schemaVersion = Number(obj.schemaVersion) || 0;
  switch (schemaVersion) {
    case 1:
      return migrateProjectSnapshotV1ToCurrent(obj);
    default:
      return null;
  }
}
